// WebMap.tsx
import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import { Coordinates } from "../types/restaurant.types";

interface WebMapProps {
  coordinates: Coordinates;
  zoomLevel: number;
  onPress: (coords: Coordinates) => void;
  style: any;
}

const WebMap: React.FC<WebMapProps> = ({
  coordinates,
  zoomLevel,
  onPress,
  style,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);

  useEffect(() => {
    if (!map.current && mapContainer.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v11",
        center: [coordinates.longitude, coordinates.latitude],
        zoom: zoomLevel,
      });

      marker.current = new mapboxgl.Marker({
        draggable: true,
      })
        .setLngLat([coordinates.longitude, coordinates.latitude])
        .addTo(map.current);

      map.current.on("click", (e) => {
        const { lng, lat } = e.lngLat;
        onPress({ longitude: lng, latitude: lat });
        marker.current?.setLngLat([lng, lat]);
      });

      marker.current.on("dragend", () => {
        const { lng, lat } = marker.current!.getLngLat();
        onPress({ longitude: lng, latitude: lat });
      });
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (map.current) {
      map.current.setZoom(zoomLevel);
    }
  }, [zoomLevel]);

  useEffect(() => {
    if (map.current && marker.current) {
      map.current.setCenter([coordinates.longitude, coordinates.latitude]);
      marker.current.setLngLat([coordinates.longitude, coordinates.latitude]);
    }
  }, [coordinates]);

  return <div ref={mapContainer} style={{ ...style, height: "100%" }} />;
};

export default WebMap;
